/* ============================================================
   YOUTUBE PLAYER LAYER (youtube.js)
   ------------------------------------------------------------
   Same public interface as VimeoPlayer (play / pause / setMuted /
   seekToPercent / destroy + onTimeUpdate/onPlay/onPause), built on
   YouTube's iframe widget postMessage API — no SDK download.

   WHY THIS EXISTS: the production plan is Vimeo (cleaner, chrome-
   free embeds), but Obed's real catalog lives on YouTube today, so
   the prototype populates from there. Any feed item with a
   `youtubeId` uses this player; `vimeoId` items use vimeo.js.
   Swapping a slide to Vimeo later = replace youtubeId with vimeoId
   in js/data.js. Nothing else changes.

   NOTE: YouTube embeds show brief YouTube chrome (title/logo) on
   load & pause — that's a platform constraint, and one reason to
   move flagship content to Vimeo for launch.
   ============================================================ */

const YT_ORIGIN = 'https://www.youtube.com';

class YouTubePlayer {
  /**
   * @param {HTMLElement} mount  container to inject the iframe into
   * @param {string} videoId    YouTube video ID
   * @param {object} callbacks  { onTimeUpdate({seconds,percent,duration}), onPlay(), onPause() }
   */
  constructor(mount, videoId, callbacks = {}) {
    this.videoId = videoId;
    this.callbacks = callbacks;
    this.ready = false;
    this.queued = [];
    this.duration = 0;

    this.iframe = document.createElement('iframe');
    this.iframe.className = 'video-iframe';
    this.iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    this.iframe.setAttribute('frameborder', '0');
    this.iframe.setAttribute('title', 'video player');
    this.iframe.src = this.buildSrc();
    mount.appendChild(this.iframe);

    this._onMessage = this._onMessage.bind(this);
    window.addEventListener('message', this._onMessage);
    // Handshake: tell the widget we're listening (retry until it answers)
    this._hello = setInterval(() => this._postRaw({ event: 'listening', channel: 'widget' }), 300);
  }

  buildSrc() {
    const params = new URLSearchParams({
      enablejsapi: '1',
      playsinline: '1',
      controls: '0',
      rel: '0',
      modestbranding: '1',
      iv_load_policy: '3',
      fs: '0',
      disablekb: '1',
      mute: '1',
      loop: '1',
      playlist: this.videoId,   // required for single-video loop
    });
    // origin param only valid on http(s) — skip for file:// previews
    if (location.origin && location.origin.startsWith('http')) {
      params.set('origin', location.origin);
    }
    return `${YT_ORIGIN}/embed/${this.videoId}?${params}`;
  }

  /* ---- postMessage plumbing ---- */
  _postRaw(obj) {
    try { this.iframe.contentWindow.postMessage(JSON.stringify(obj), YT_ORIGIN); }
    catch (_) { /* iframe gone */ }
  }
  _send(func, args = []) {
    if (!this.ready) { this.queued.push([func, args]); return; }
    this._postRaw({ event: 'command', func, args });
  }

  _onMessage(e) {
    if (e.origin !== YT_ORIGIN || e.source !== this.iframe.contentWindow) return;
    let data;
    try { data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; }
    catch (_) { return; }

    if (!this.ready && (data.event === 'onReady' || data.event === 'initialDelivery' || data.event === 'infoDelivery')) {
      this.ready = true;
      clearInterval(this._hello);
      this.queued.forEach(([f, a]) => this._send(f, a));
      this.queued = [];
    }

    const info = data.info;
    if (!info) return;
    if (typeof info.duration === 'number' && info.duration > 0) {
      this.duration = info.duration;
    }
    if (typeof info.currentTime === 'number') {
      this.callbacks.onTimeUpdate?.({
        seconds: info.currentTime,
        percent: this.duration ? info.currentTime / this.duration : 0,
        duration: this.duration,
      });
    }
    if (typeof info.playerState === 'number') {
      if (info.playerState === 1) this.callbacks.onPlay?.();
      else if (info.playerState === 2) this.callbacks.onPause?.();
    }
  }

  /* ---- public controls (same contract as VimeoPlayer) ---- */
  play()           { this._send('playVideo'); }
  pause()          { this._send('pauseVideo'); }
  setMuted(m)      { this._send(m ? 'mute' : 'unMute'); }
  seekToPercent(p) {
    if (this.duration) this._send('seekTo', [this.duration * Math.min(Math.max(p, 0), 1), true]);
  }

  destroy() {
    clearInterval(this._hello);
    window.removeEventListener('message', this._onMessage);
    this.iframe.remove();
  }
}
